import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TbButtonComponent } from '../components/tb-button.component';
import { TbCardComponent } from '../components/tb-card.component';
import { TbThemeImageComponent } from '../components/tb-theme-image.component';
import { TbTextFieldComponent } from '../forms/tb-text-field.component';
import { DEFAULT_LOGIN_PRESENTATION, LoginPresentation, LoginSubmitStrategy } from './login-presentation.model';
import { LoginNotice, parseLoginNotice } from './login-notice.model';

/** Material Symbols glyph per notice severity (decorative; the text carries the meaning). */
const NOTICE_ICON: Readonly<Record<LoginNotice['severity'], string>> = {
  info: 'info',
  warning: 'warning',
  critical: 'error',
};

/**
 * THE ONE login screen (DESIGN.md §6). PROMOTED into @toybank/ui (2026-08-18) from
 * toybank-app's login/login-screen.component.ts, where it was built to unify the real portal's
 * pre-auth screen with the debug wrapper's (ADR-0105) per-target login preview under owner ruling
 * (2026-08-18): "[the debug wrapper's login and the real toybank.localhost login] need to be the
 * same page, shared fully. The Debug wrapper should not have its own alternate login pages."
 *
 * Promoting it here extends that same rule to a THIRD surface: the Back-Office Console
 * (`backoffice`), which previously had its own bespoke "Staff sign-in" card. All three surfaces
 * now render the identical hero + brand + tagline + username field + submit button structure --
 * branding via the injected `presentation` input (see login-presentation.model.ts's
 * LOGIN_PRESENTATION), and behavior via the injected `strategy` (the ONLY real divergence: what
 * clicking the primary action does -- redirect to Keycloak, POST /bff/debug/login, etc.).
 *
 * HERO ARTWORK (2026-08-22). The hero panel used to be an accent gradient with the surface's
 * Material icon floating in it -- the same block five times in five colours. It now renders the
 * landing image named by `presentation().heroSrc`, and the artwork carries a deliberate signal:
 * the surfaces backed by REAL data (Main Portal, Backoffice) are real architectural photography,
 * while the ones backed by NOT-real data (Demo Portal, Demo Backoffice, Simulator) are photographs
 * of scale models. You can tell you are looking at a model before you read a word -- which is why
 * the images are given a real `alt` rather than being hidden from assistive tech as decoration:
 * "a scale model of..." carries the demo-vs-real distinction to a screen reader too.
 *
 * The gradient survives as the panel background behind the image, so a presentation with no
 * `heroSrc` still renders exactly as it did before (icon + gradient), and the panel reads as the
 * right colour while the image decodes.
 *
 * STATUS MESSAGE (2026-10-02). The optional `notice` input renders an operator status message
 * (edge-bff GET /bff/config/login-notice, Unleash flag `toybank.login.status-message`) between the
 * brand and the card, in the centre column -- the only part visible at every width. The value is
 * re-validated here with parseLoginNotice, so a host cannot pass an unchecked link; anything
 * invalid renders nothing. Text and links are plain bindings (no innerHTML). Colours are tokens
 * only, so the message follows the resolved theme -- including the debug wrapper's pre-login
 * phase, which has no `data-theme` and relies on the `prefers-color-scheme` token fallback.
 */
@Component({
  selector: 'tb-login-screen',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TbCardComponent,
    TbButtonComponent,
    TbTextFieldComponent,
    TbThemeImageComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="login-screen" [style.--login-accent]="presentation().accent" data-testid="login-screen">
      <div class="login-screen-hero">
        @if (presentation().heroSrc) {
          <img
            class="login-screen-hero-img"
            [src]="presentation().heroSrc"
            [alt]="presentation().heroAlt ?? ''"
            data-testid="login-screen-hero-img"
            fetchpriority="high"
            decoding="async"
          />
          <div class="login-screen-hero-scrim" aria-hidden="true"></div>
        } @else {
          <span class="material-symbols-outlined login-screen-hero-icon" aria-hidden="true">{{ presentation().icon }}</span>
        }
      </div>
      <div class="login-screen-center">
        <div class="login-screen-brand">
          @if (presentation().wordmarkSrcLight) {
            <!-- Theme-swapped (2026-09-20 light-mode visibility fix): a hardcoded single asset
                 rendered the dark-surface (light-ink) wordmark invisibly on a light-resolved
                 surface. See TbThemeImageComponent for why this is CSS-only, not ThemeService-driven. -->
            <tb-theme-image
              [srcDark]="presentation().wordmarkSrc!"
              [srcLight]="presentation().wordmarkSrcLight!"
              [alt]="presentation().wordmarkAlt ?? ''"
              imgStyle="width: min(22rem, 100%); height: auto;"
            />
          } @else if (presentation().wordmarkSrc) {
            <img [src]="presentation().wordmarkSrc" [alt]="presentation().wordmarkAlt" class="login-screen-wordmark" />
          } @else {
            <span class="material-symbols-outlined login-screen-badge" aria-hidden="true">{{ presentation().icon }}</span>
            <span class="login-screen-title" data-testid="login-screen-title">{{ presentation().title }}</span>
          }
        </div>
        @if (safeNotice(); as n) {
          <div
            class="login-screen-notice"
            data-testid="login-notice"
            [attr.data-severity]="n.severity"
            [attr.role]="n.severity === 'critical' ? 'alert' : 'status'"
          >
            <span class="material-symbols-outlined login-screen-notice-icon" aria-hidden="true">{{ noticeIcon[n.severity] }}</span>
            <!-- Links sit in their own wrapping row under the text, so a label never fuses with the
                 last word of the text or of the previous label. The {{ ' ' }} nodes keep a real
                 space between them for copy/paste and textContent; a flex container does not lay
                 out whitespace-only text, so they cost nothing visually. -->
            <div class="login-screen-notice-body">
              <span class="login-screen-notice-text" data-testid="login-notice-text">{{ n.text }}</span>
              @if (n.links.length) {
                {{ ' ' }}<span class="login-screen-notice-links">
                  @for (link of n.links; track $index) {
                    @if (!$first) { {{ ' ' }} }
                    <a
                      class="login-screen-notice-link"
                      [href]="link.href"
                      rel="noopener noreferrer"
                      referrerpolicy="no-referrer"
                      [attr.data-testid]="'login-notice-link-' + $index"
                    >{{ link.label }}</a>
                  }
                </span>
              }
            </div>
          </div>
        }
        <tb-card heading="">
          <p class="login-screen-tagline">{{ tagline() }}</p>
          <!-- xt round 2 N9: a plain (submit) handler. (ngSubmit) needs NgForm / FormGroupDirective,
               and this form has neither (ReactiveFormsModule alone, no [formGroup]), so Enter in the
               username field did a NATIVE submit -- a page reload that dropped the typed username --
               instead of calling the strategy. -->
          <form (submit)="$event.preventDefault(); onSubmit()" class="login-screen-form">
            <tb-text-field [label]="usernameLabel()" [control]="usernameControl()" [testid]="usernameTestId()" />
            <tb-button [disabled]="disabled()" [loading]="loading()" (clicked)="onSubmit()" [attr.data-testid]="submitTestId()">{{ actionLabel() }}</tb-button>
            @if (hint()) { <p class="login-screen-hint">{{ hint() }}</p> }
            @if (error()) { <p class="login-screen-err">{{ error() }}</p> }
          </form>
          @if (showRecoveryLink()) {
            <p class="login-screen-alt login-screen-recovery-row">
              <a class="login-screen-link" routerLink="/recovery/login" data-testid="cant-sign-in-link">{{ recoveryLinkLabel() }}</a>
            </p>
          }
        </tb-card>
      </div>
    </div>
  `,
  styles: `
    /* container query, not viewport media query, so the hero collapses correctly whether this
       component fills the full unauthenticated shell (real portal / backoffice) or the debug
       wrapper's narrower centre column (ADR-0105). */
    .login-screen {
      container-type: inline-size;
      container-name: tb-login-screen;
      min-height: 100%;
      display: grid;
      grid-template-columns: 1fr 480px;
      background: var(--tb-surface-bg, #0f1115);
    }
    /* The accent gradient is no longer the hero -- it is what sits BEHIND the artwork, so the
       panel still reads as this surface's colour while the image decodes, and still works for a
       presentation that carries no heroSrc at all. */
    .login-screen-hero {
      position: relative; overflow: hidden;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, color-mix(in srgb, var(--login-accent) 24%, var(--tb-surface-panel, #1a1d24)), color-mix(in srgb, var(--login-accent) 8%, var(--tb-surface-inset, #11141a)));
    }
    .login-screen-hero-img {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      /* The panel is one fraction beside a fixed 480px column, so it ranges ~1:1 to ~4:3 depending
         on viewport. The art is cut 4:3: cover fills the wide case exactly and crops least at the
         narrow one, and centring keeps the subject rather than the empty sky. */
      object-fit: cover; object-position: center;
    }
    /* Tints the artwork toward the surface accent and darkens the edge that meets the login card,
       so the seam between image and form is a gradient rather than a hard vertical line. */
    .login-screen-hero-scrim {
      position: absolute; inset: 0; pointer-events: none;
      background:
        linear-gradient(to right, transparent 55%, color-mix(in srgb, var(--tb-surface-bg, #0f1115) 85%, transparent) 100%),
        linear-gradient(160deg, color-mix(in srgb, var(--login-accent) 22%, transparent), transparent 60%);
    }
    .login-screen-hero-icon { font-size: 5rem; color: var(--login-accent); opacity: 0.9; }
    @container tb-login-screen (max-width: 760px) {
      .login-screen { grid-template-columns: 1fr; }
      .login-screen-hero { display: none; }
    }
    .login-screen-center {
      max-width: 480px; margin: 0; padding: 3rem 2rem;
      display: flex; flex-direction: column; justify-content: center;
    }
    .login-screen-brand { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.25rem; }
    .login-screen-wordmark { width: min(22rem, 100%); height: auto; display: block; }
    .login-screen-badge { font-size: 1.5rem; color: var(--login-accent); }
    .login-screen-title { font-size: 1.2rem; font-weight: 700; letter-spacing: -0.01em; color: var(--tb-text-primary, #e6e9ef); }
    .login-screen-tagline { margin: 0 0 1rem; color: var(--tb-text-muted, #97a0b0); }
    .login-screen-form { display: flex; flex-direction: column; gap: 0.7rem; max-width: 320px; }
    .login-screen-hint { margin: 0; font-size: 0.82rem; color: var(--tb-text-muted, #97a0b0); }
    .login-screen-err { margin: 0; font-size: 0.85rem; color: var(--tb-finance-negative, #f7768e); }
    .login-screen-alt { margin: 0.5rem 0 0; font-size: 0.85rem; text-align: center; }
    .login-screen-recovery-row { margin-top: 0.75rem; padding-top: 0.5rem; border-top: 1px solid var(--tb-border-line, #2a2f3a); }
    .login-screen-link {
      background: none; border: none; padding: 0; cursor: pointer;
      color: var(--tb-brand-accent, #4f86f7); font-size: 0.85rem; text-decoration: underline;
    }
    .login-screen-link:hover { opacity: 0.8; }
    /* Status message. Tone = one token per severity; the tint, border and icon derive from it and
       the text stays --tb-text-primary, so contrast holds in both ramps (_tb-theme.scss):
       text/links on the tint (10%; warning 16%) 11.7-13.7:1 in both themes; tone border/icon vs the
       tint 4.4-7.1:1 (lowest: light warning #855c00, 4.40:1). No :host-context([data-theme]) rules. */
    .login-screen-notice {
      --login-notice-tone: var(--tb-status-info, #7aa2f7);
      --login-notice-tint: 10%;
      display: flex; align-items: flex-start; gap: 0.6rem;
      margin: 0 0 1rem; padding: 0.7rem 0.85rem;
      border: 1px solid var(--login-notice-tone);
      border-left-width: 4px;
      border-radius: var(--tb-radius-md, 12px);
      background: color-mix(in srgb, var(--login-notice-tone) var(--login-notice-tint), var(--tb-surface-bg, #0f1115));
      color: var(--tb-text-primary, #e6e9ef);
      font-size: 0.875rem; line-height: 1.5;
    }
    /* Warning gets a stronger tint: at 10% the light-theme amber read as grey-khaki. */
    .login-screen-notice[data-severity='warning'] {
      --login-notice-tone: var(--tb-status-warning, #e0af68);
      --login-notice-tint: 16%;
    }
    .login-screen-notice[data-severity='critical'] { --login-notice-tone: var(--tb-finance-negative, #f7768e); }
    .login-screen-notice-icon { font-size: 1.25rem; line-height: 1.2; color: var(--login-notice-tone); flex: none; }
    .login-screen-notice-body { margin: 0; min-width: 0; overflow-wrap: break-word; }
    .login-screen-notice-links {
      display: flex; flex-wrap: wrap; gap: 0.4rem 0.9rem;
      margin-top: 0.4rem;
    }
    .login-screen-notice-link {
      color: var(--tb-text-primary, #e6e9ef);
      font-weight: 600; text-decoration: underline; text-underline-offset: 2px;
      overflow-wrap: break-word; min-width: 0;
      padding: 0 2px; margin: 0 -2px;
      -webkit-box-decoration-break: clone; box-decoration-break: clone;
    }
    .login-screen-notice-link:hover { text-decoration-thickness: 2px; }
    .login-screen-notice-link:focus-visible {
      outline: 2px solid var(--login-notice-tone); outline-offset: 1px; border-radius: 2px;
    }
  `,
})
export class TbLoginScreenComponent {
  readonly presentation = input<LoginPresentation>(DEFAULT_LOGIN_PRESENTATION);
  readonly tagline = input.required<string>();
  readonly usernameControl = input.required<FormControl<string>>();
  readonly usernameLabel = input('Username');
  readonly usernameTestId = input('login-user');
  readonly actionLabel = input.required<string>();
  readonly submitTestId = input<string | undefined>(undefined);
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly hint = input('');
  readonly error = input<string | null>(null);
  readonly showRecoveryLink = input(false);
  readonly recoveryLinkLabel = input('');
  readonly strategy = input.required<LoginSubmitStrategy>();
  /** Optional sign-in page status message (see the class doc). Re-validated before rendering. */
  readonly notice = input<LoginNotice | null>(null);
  protected readonly safeNotice = computed(() => parseLoginNotice(this.notice()));
  protected readonly noticeIcon = NOTICE_ICON;

  onSubmit(): void {
    this.strategy().submit();
  }
}
